import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Control, Controller, FieldErrors, useForm, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import Select from 'react-select';
import { differenceInDays, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { useShowBookingV4 } from '../../../../services/create-booking-v4/hooks/useShowBookingV4';
import useBookingChannexMapping from '../../../../services/booking/hooks/useBookingChannexMapping';
import useCheckAvi from '../../../../services/booking/hooks/useCheckAvi';
import { CheckAviResponseType, RoomTypeData } from '../../../../services/booking/response/CheckAviResponse';
import { useFixIncompleteBookingV4 } from '../../../../services/create-booking-v4/hooks/useFixIncompleteBookingV4';

interface FixIncompleteBookingFormProps {
    bookingRoomId: string;
    onClose: () => void;
    onSuccess?: () => void;
    onOpenFullEdit?: (bookingId: string) => void;
}

interface FixIncompleteBookingFormValues {
    room_type_id: string;
    room_id: string;
    rate_plan_id: string;
}

const formatRupiah = (value: number) => 'Rp ' + new Intl.NumberFormat('id-ID').format(Math.round(value || 0));

const FixIncompleteBookingForm: React.FC<FixIncompleteBookingFormProps> = ({ bookingRoomId, onClose, onSuccess, onOpenFullEdit }) => {
    const queryClient = useQueryClient();

    const { data: bookingRes, isLoading: isBookingLoading, isError: isBookingError } = useShowBookingV4({ bookingId: bookingRoomId, enabled: !!bookingRoomId });
    const { data: channexRes } = useBookingChannexMapping(bookingRoomId);

    // Endpoint GET /v4/booking/{id} mengembalikan objek reservasi berisi array
    // `booking` (per-kamar). Untuk kasus Fix Incomplete Booking, baris kamar yang
    // relevan adalah yang id-nya sama dengan bookingRoomId yang diklik user.
    const booking = useMemo(() => {
        const data = bookingRes?.data;
        if (!data) return null;
        return data.booking.find((room) => room.id === bookingRoomId) ?? data.booking[0] ?? null;
    }, [bookingRes, bookingRoomId]);

    const channexMapping = channexRes?.data;
    const hasChannexInfo = !!channexMapping && (!!channexMapping.channex_room_type_id || !!channexMapping.channex_rate_plan_id);

    const nights = useMemo(() => {
        if (!booking) return 0;
        try {
            return differenceInDays(parseISO(booking.checkout_date), parseISO(booking.checkin_date));
        } catch {
            return 0;
        }
    }, [booking]);

    const ratesCount = booking?.rates?.length ?? 0;
    const isRateInconsistent = !!booking && nights !== ratesCount;

    const { control, watch, setValue, formState: { errors }, setError, handleSubmit, reset } = useForm<FixIncompleteBookingFormValues>({
        defaultValues: { room_type_id: '', room_id: '', rate_plan_id: '' },
    });

    useEffect(() => {
        if (booking) {
            reset({
                room_type_id: booking.room_type_id ?? '',
                room_id: booking.room_id ?? '',
                rate_plan_id: booking.rate_plan_id ?? '',
            });
        }
    }, [booking, reset]);

    const { data: roomAvi, isFetching: isAviFetching } = useCheckAvi({
        checkin_date: booking?.checkin_date ?? '',
        checkout_date: booking?.checkout_date ?? '',
        booking_id: bookingRoomId,
    });

    const { mutate, isPending } = useFixIncompleteBookingV4();
    const [formError, setFormError] = useState<string | null>(null);

    const onSubmit = useCallback((values: FixIncompleteBookingFormValues) => {
        if (!booking) return;

        setFormError(null);
        toast.loading('Saving...', { id: 'fix-incomplete-booking' });

        mutate({
            bookingId: booking.id,
            formData: {
                room_type_id: values.room_type_id,
                rate_plan_id: values.rate_plan_id,
                room_id: values.room_id,
                checkin_date: booking.checkin_date,
                checkout_date: booking.checkout_date,
                // Dikirim apa adanya dari data booking asli, TIDAK dihitung ulang di sini.
                rates: booking.rates.map((r) => ({ id: r.id, date: r.date, rate: Number(r.rate) })),
            },
        }, {
            onSuccess: async () => {
                toast.success('Booking fixed successfully', { id: 'fix-incomplete-booking' });

                // GREP tidak menemukan nama query key baru khusus untuk widget ini di luar
                // yang sudah dipakai hook lain, jadi invalidasi mengikuti key yang memang
                // dipakai oleh dashboard incomplete-bookings widget & data kalender booking.
                await queryClient.invalidateQueries({ queryKey: ['dashboard-incomplete-bookings'] });
                await queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings', 'availability', 'bookings-v4'].includes(key as string)),
                });

                onSuccess?.();
                onClose();
            },
            onError: (mutationError: unknown) => {
                const axiosError = mutationError as { response?: { status?: number; data?: { errors?: Record<string, string[]>; message?: string } } };
                if (axiosError?.response?.status === 422) {
                    const validationErrors = axiosError?.response?.data?.errors;
                    if (validationErrors) {
                        let matchedKnownField = false;
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            if (field === 'room_type_id' || field === 'room_id' || field === 'rate_plan_id') {
                                matchedKnownField = true;
                                setError(field as keyof FixIncompleteBookingFormValues, {
                                    type: 'manual',
                                    message: (messages as string[])[0],
                                });
                            }
                        }
                        if (!matchedKnownField) {
                            const firstMessage = Object.values(validationErrors)[0] as string[] | undefined;
                            setFormError(firstMessage?.[0] || 'There are errors in the form, please check and try again.');
                        }
                    }
                    toast.error('There are errors in the form, please check and try again', { id: 'fix-incomplete-booking' });
                } else {
                    setFormError(axiosError?.response?.data?.message || 'Failed to save changes.');
                    toast.error('Failed to save changes', { id: 'fix-incomplete-booking' });
                }
            },
        });
    }, [mutate, booking, onClose, onSuccess, queryClient, setError]);

    if (isBookingLoading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (isBookingError || !booking) {
        return <Alert variant="danger">Failed to load booking data.</Alert>;
    }

    return (
        <div>
            {/* Info panel read-only */}
            <div className="mb-3 p-3 border rounded bg-light">
                <div className="fw-bold">{booking.customer_name}</div>
                <div className="text-muted small">
                    Invoice: {booking.invoice_number || '-'} &middot; {booking.checkin_date} &ndash; {booking.checkout_date}
                </div>

                {hasChannexInfo && (
                    <div className="mt-2 pt-2 border-top">
                        <div className="fw-bold small text-muted mb-1">Original data from OTA{channexMapping?.ota_name ? ` (${channexMapping.ota_name})` : ''}</div>
                        <div className="small">
                            <span className="text-muted">Room Type: </span>
                            {channexMapping?.channex_room_type_name ? (
                                <>
                                    {channexMapping.channex_room_type_name}{' '}
                                    {channexMapping.channex_room_type_id && (
                                        <span className="text-muted" style={{ fontFamily: 'monospace', fontSize: '11px' }}>({channexMapping.channex_room_type_id})</span>
                                    )}
                                </>
                            ) : channexMapping?.channex_room_type_id ? (
                                <>
                                    <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{channexMapping.channex_room_type_id}</span>
                                    <div className="text-muted" style={{ fontSize: '10px' }}>name could not be retrieved from Channex right now</div>
                                </>
                            ) : '-'}
                        </div>
                        <div className="small">
                            <span className="text-muted">Rate Plan: </span>
                            {channexMapping?.channex_rate_plan_name ? (
                                <>
                                    {channexMapping.channex_rate_plan_name}{' '}
                                    {channexMapping.channex_rate_plan_id && (
                                        <span className="text-muted" style={{ fontFamily: 'monospace', fontSize: '11px' }}>({channexMapping.channex_rate_plan_id})</span>
                                    )}
                                </>
                            ) : channexMapping?.channex_rate_plan_id ? (
                                <>
                                    <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{channexMapping.channex_rate_plan_id}</span>
                                    <div className="text-muted" style={{ fontSize: '10px' }}>name could not be retrieved from Channex right now</div>
                                </>
                            ) : '-'}
                        </div>
                    </div>
                )}
            </div>

            {isRateInconsistent ? (
                <>
                    <Alert variant="warning">
                        This reservation&apos;s rate data is inconsistent ({ratesCount} rate row{ratesCount === 1 ? '' : 's'} for {nights} night{nights === 1 ? '' : 's'}).
                        Fix the rates first via Edit Reservation, then the room type &amp; rate plan mapping can be saved.
                    </Alert>
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                        {onOpenFullEdit && (
                            <Button variant="primary" onClick={() => onOpenFullEdit(booking.id)}>Open Edit Reservation</Button>
                        )}
                    </div>
                </>
            ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                    {formError && <Alert variant="danger">{formError}</Alert>}

                    <SelectRoomType control={control} roomType={roomAvi} setValue={setValue} errors={errors} isLoading={isAviFetching} />

                    {watch('room_type_id') && (
                        <>
                            <SubroomComponent watch={watch} control={control} roomType={roomAvi} errors={errors} />
                            <RateplansComponent control={control} watch={watch} roomType={roomAvi} errors={errors} />
                        </>
                    )}

                    <div className="mt-3">
                        <div className="fw-bold small mb-1">Rate Summary</div>
                        <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                            {booking.rates.map((r) => (
                                <div key={r.id} className="d-flex justify-content-between small py-1 border-bottom">
                                    <span>{r.date}</span>
                                    <span>{formatRupiah(Number(r.rate))}</span>
                                </div>
                            ))}
                        </div>
                        <div className="d-flex justify-content-between fw-bold small mt-1">
                            <span>Total</span>
                            <span>{formatRupiah(booking.rates.reduce((sum, r) => sum + Number(r.rate), 0))}</span>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button variant="secondary" onClick={onClose} disabled={isPending}>Cancel</Button>
                        <Button
                            type="submit"
                            disabled={isPending || !watch('room_type_id') || !watch('rate_plan_id')}
                        >
                            {isPending ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    <span className="ms-1">Saving...</span>
                                </>
                            ) : 'Submit'}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default FixIncompleteBookingForm;

interface SelectRoomTypeProps {
    control: Control<FixIncompleteBookingFormValues>;
    roomType?: CheckAviResponseType;
    setValue: UseFormSetValue<FixIncompleteBookingFormValues>;
    errors: FieldErrors<FixIncompleteBookingFormValues>;
    isLoading: boolean;
}

interface RoomTypeOption {
    value: string;
    label: string;
    isDisabled?: boolean;
}

const SelectRoomType: React.FC<SelectRoomTypeProps> = ({ control, roomType, setValue, errors, isLoading }) => {
    const [roomTypeOptions, setRoomTypeOptions] = useState<RoomTypeOption[]>([]);

    useEffect(() => {
        if (roomType?.data && roomType.data.length > 0) {
            setRoomTypeOptions(roomType.data.map((room: RoomTypeData) => ({
                value: room.id,
                label: room.available < 1 ? `${room.name} (fully booked)` : room.name,
                isDisabled: room.available < 1,
            })));
        }
    }, [roomType]);

    return (
        <div className="mb-3">
            <label className="col-form-label pt-0" htmlFor="room_type_id">Room Type:</label>
            <Controller
                control={control}
                name="room_type_id"
                rules={{ required: true }}
                render={({ field }) => (
                    <Select
                        isLoading={isLoading}
                        className={clsx('', { 'is-invalid': errors.room_type_id })}
                        options={roomTypeOptions}
                        value={roomTypeOptions.find((room) => room.value === field.value) || null}
                        onChange={(selectedOption) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.value);
                                setValue('room_id', '');
                                setValue('rate_plan_id', '');
                            }
                        }}
                        noOptionsMessage={() => 'No room types available'}
                    />
                )}
            />
            {errors.room_type_id && <div className="invalid-feedback d-block">Room type is required</div>}
        </div>
    );
};

interface SubroomComponentProps {
    watch: UseFormWatch<FixIncompleteBookingFormValues>;
    control: Control<FixIncompleteBookingFormValues>;
    roomType?: CheckAviResponseType;
    errors: FieldErrors<FixIncompleteBookingFormValues>;
}

const SubroomComponent: React.FC<SubroomComponentProps> = ({ watch, control, roomType, errors }) => {
    const [subroomOptions, setSubroomOptions] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        const roomTypeId = watch('room_type_id');
        if (roomTypeId && roomType?.data) {
            const roomData = roomType.data.find((room: RoomTypeData) => room.id === roomTypeId);
            if (roomData) {
                let rooms = roomData.rooms?.map((room) => ({
                    value: room.id,
                    label: room.room_number,
                })) || [];

                rooms = [{ value: '', label: 'Unassigned Room' }, ...rooms];

                setSubroomOptions(rooms);
            }
        }
    }, [watch('room_type_id'), roomType]);

    return (
        <div className="mb-3">
            <label className="col-form-label" htmlFor="room_id">Subroom / Room Number:</label>
            <Controller
                control={control}
                name="room_id"
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_id })}
                        options={subroomOptions}
                        value={subroomOptions.find((room) => room.value === field.value) || null}
                        onChange={(selectedOption) => {
                            field.onChange(selectedOption?.value ?? '');
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
        </div>
    );
};

interface RateplansComponentProps {
    control: Control<FixIncompleteBookingFormValues>;
    watch: UseFormWatch<FixIncompleteBookingFormValues>;
    roomType?: CheckAviResponseType;
    errors: FieldErrors<FixIncompleteBookingFormValues>;
}

const RateplansComponent: React.FC<RateplansComponentProps> = ({ control, watch, roomType, errors }) => {
    const [ratePlanOptions, setRatePlanOptions] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        const roomTypeId = watch('room_type_id');
        if (roomTypeId && roomType?.data) {
            const roomData = roomType.data.find((room: RoomTypeData) => room.id === roomTypeId);
            if (roomData?.rate_plans) {
                setRatePlanOptions(roomData.rate_plans.map((rate) => ({
                    value: rate.id,
                    label: rate.name,
                })));
            }
        }
    }, [watch('room_type_id'), roomType]);

    return (
        <div className="mb-3">
            <label className="col-form-label" htmlFor="rate_plan_id">Rate Plan:</label>
            <Controller
                control={control}
                name="rate_plan_id"
                rules={{ required: true }}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.rate_plan_id })}
                        options={ratePlanOptions}
                        value={ratePlanOptions.find((rate) => rate.value === field.value) || null}
                        onChange={(selectedOption) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.value);
                            }
                        }}
                        noOptionsMessage={() => 'No rate plans available'}
                    />
                )}
            />
            {errors.rate_plan_id && <div className="invalid-feedback d-block">Rate plan is required</div>}
        </div>
    );
};
